// The Cattle Ticker on the redesigned raisedbeef.com homepage.
//
// Sibling of rbHomeKpis.ts and rbTools.ts, and bound by the same two rules for
// the same reasons: it is shared by the build-time prerender AND the client
// refresh, so the HTML Google reads cannot disagree with the HTML a visitor
// ends up with; and it deliberately does NOT import
// @/integrations/supabase/client, which touches `localStorage` at module scope
// and throws under Node during prerender.
//
// ── Two things here are load-bearing and non-obvious ──────────────────────
//
// 1. EVERY DISPLAYED STRING IS FORMATTED HERE, not in the component.
//    This page is prerendered under Node (UTC, on a build machine) and then
//    hydrated in a rancher's browser (Central, Mountain, wherever). Any
//    formatting that consults the ambient locale or timezone produces a
//    different string in those two passes, and React fails hydration for the
//    WHOLE document — which on this page means the front door of the company
//    falls back to client rendering. So times are formatted in an EXPLICIT
//    timezone and numbers with an explicit locale, once, here.
//
// 2. TIMES ARE CENTRAL, ALWAYS — not the visitor's zone.
//    A sale barn's ring runs on its own clock and the industry quotes Central.
//    Showing a Nebraska lot as "4:36 PM" to a Californian because their laptop
//    is on Pacific would misdate the print by two hours against every other
//    source they read. Central is also what the RPC's own day boundary uses
//    (America/Chicago), so the timestamp and the day it belongs to agree.
//
// Nothing here throws or rejects. On any failure it returns the empty payload,
// so the homepage renders its chrome, its tools and an honest "no sales
// posted" rather than failing the build or showing an error.

import { barnSlug } from "@/lib/barnSlug";
import { STATE_NAME_TO_ALPHA } from "@/lib/regions";

/** How the tape is read: the RPCs behind it, and the scope they take. */
export interface TickerScope {
  /** Two-letter state, or null for the whole country. */
  state?: string | null;
  /** Exact `sale_barn_name`, or null for every barn in scope. */
  barn?: string | null;
  /** A barn SLUG, which resolves to every name that barn has traded under.
   *  Prefer this over `barn` whenever a slug is what you have: ok-stateline-
   *  stockyards trades as both "Stateline Stockyards" and "StateLine
   *  Stockyards", and matching one name showed 177 of its 946 lots. */
  barnSlug?: string | null;
  /**
   * SEVERAL barn slugs — the RaisedBeef app's "My Barns" board.
   *
   * ⚠️ NOT A CLIENT-SIDE FILTER, AND IT CANNOT BE ONE. rpc_cattle_ticker
   * returns at most `perBand` lots per weight class, five by default. A barn
   * that sold forty head at 500 lb and did not crack the national top five is
   * not in the payload at all, so narrowing the RESULT would print "No sales"
   * over a band where the man's own barn really sold. The RPCs take the list
   * and narrow before they rank.
   *
   * The DAY is resolved from the set: the most recent day on which ANY of
   * these barns sold.
   */
  barnSlugs?: string[] | null;
  /** 'Steers' | 'Heifers' | 'All' (steers + heifers). The ticker has no cows or bulls. */
  cattleType?: string;
  /** Lots smaller than this are noise on a national board; the RPC default is 10. */
  minHead?: number;
  /** Lots per band. 5 for a board; a large number to read a whole sale day,
   *  which is how the barn and state pages fill their "all lots" list without
   *  a second, differently-filtered query that could disagree with the board. */
  perBand?: number;
  /** A specific day, or null for the last day that sold. */
  saleDate?: string | null;
  /**
   * THE BARN BOARD ONLY (owner, 2026-10-31): list every lot as it sells, the
   * one lot rule reported on each lot (`counts`, `minHead`) rather than
   * applied, so a barn selling in ones and twos is never blank while live.
   * The band averages still come from rpc_cattle_ticker_bands, on the rule —
   * one average everywhere. Reads rpc_cattle_ticker_every_lot (20261031000002).
   */
  everyLot?: boolean;
}

/** One printed lot, entirely pre-formatted. */
export interface TickerLot {
  /** Stable identity for React keys and for diffing a refresh against what is
   *  on screen — which is what lets a newly-arrived lot animate in. */
  id: string;
  head: number;
  /** "14 hd" */
  headLabel: string;
  /** "502 lb" */
  weightLabel: string;
  /** "$490.00" */
  priceLabel: string;
  /** "$2,459.80" — what the lot actually brought per head. See below. */
  perHeadLabel: string;
  /** "5:36 PM" — Central, always. */
  timeLabel: string;
  /** The raw ISO timestamp. Kept alongside the formatted one because the
   *  crawling tape shows prints in ARRIVAL order across every band, and a
   *  "5:36 PM" string cannot be sorted — 12:05 PM would come before 5:36 AM. */
  at: string;
  barn: string;
  state: string;
  /** Raw class from the data: "Steers" | "Heifers" | "Cows" | "Bulls". */
  cattleType: string;
  /** The badge's text — SINGULAR, because it describes this one lot: "Steer",
   *  "Heifer", "Cow", "Bull". Empty when the class is missing or unrecognised,
   *  so the UI renders no badge rather than an empty one.
   *
   *  Formatted here with every other displayed string. It needs no locale, but
   *  keeping the rule absolute is what stops the next person adding a
   *  toLocaleString() in a component and failing hydration. */
  classLabel: string;
  /** Would this lot count under the one lot rule (5 under 500 lb, 10 at 500 and
   *  over)? Always true from rpc_cattle_ticker, which applies the rule; false on
   *  a small lot the barn board lists anyway (`everyLot`). Absent where a
   *  caller builds lots from another reader: absent means it counts. */
  counts?: boolean;
  /** The head floor the rule holds this lot to — 5 or 10 — from the database,
   *  never restated here; null or absent where the reader did not report it. */
  minHead?: number | null;
}

/** A 100 lb weight band: its aggregate, then its latest lots. */
export interface TickerBand {
  /** 300, 400, … 900 */
  band: number;
  /** "300–399" */
  label: string;
  /** "$525.66", or "—" when the band had no qualifying sales. */
  vwapLabel: string;
  /** "$385 – $590", or "" when there is nothing to range. */
  rangeLabel: string;
  /** "12 lots · 378 hd", or "" when empty. */
  depthLabel: string;
  /** "1,678 hd" on its own — the head the band's weighted average is struck
   *  over. The app's average row says it, because "weighted average" without a
   *  denominator reads as an average of the few pens shown above it, and it is
   *  not: it is every qualifying lot in the band.
   *
   *  Optional for the same reason the raw figures are: a caller building bands
   *  out of lot rows (toBands) has no band aggregate, and must not print a head
   *  count that describes only the rows it happens to hold. */
  headLabel?: string;
  /** Newest first, at most what the RPC was asked for. */
  lots: TickerLot[];

  /**
   * The band's aggregate figures, RAW — the same numbers `vwapLabel`,
   * `rangeLabel` and `depthLabel` are built from.
   *
   * ⚠️ THESE ARE FOR ARITHMETIC AND GEOMETRY, NEVER FOR DISPLAY. Rule 1 at the
   * top of this file stands: every string a visitor reads is formatted here,
   * once, so the prerender and the browser cannot disagree. A chart has to
   * place a point at a price and a range bar has to know how far along the
   * average sits, and neither can be done by parsing "$414.77" back apart —
   * which is precisely the trick that once put a barn with no price at the top
   * of the steer board (see steer-tracker/toBands.ts).
   *
   * Optional because `toBands` builds bands from lot rows and deliberately has
   * no aggregate: averaging the five printed lots would be twenty minutes of
   * sample wearing the label of the day.
   */
  vwap?: number | null;
  low?: number | null;
  high?: number | null;
  head?: number;
  lotCount?: number;
}

/** The scoreboard above the tape. */
export interface TickerSummary {
  /** "37" */
  barns: string;
  /** "6,391" */
  lots: string;
  /** "27,895" */
  head: string;
  /** Steers / heifers / cows / bulls, pre-formatted with thousands separators.
   *  `head` is the same figure RAW — the map's receipts panel draws a bar in
   *  proportion to it, and the alternative was parsing "13,441" back out of
   *  `value`, which is exactly the trick that put a barn with no price at the
   *  top of the steer board (see steer-tracker/toBands.ts). */
  mix: { label: string; value: string; head: number }[];
  /** Head against a TYPICAL same-weekday, e.g. "108". Null when there is no
   *  usable baseline. Unsigned — `dir` carries the direction. */
  vsTypical: string | null;
  dir: "up" | "down" | null;
  /** True while the day is still selling. The comparison is then cut at the
   *  hour reached so far on BOTH sides, so the UI must say "by this hour" —
   *  without it the figure reads as a whole-day claim. */
  partial: boolean;
  /**
   * The COMPARABLE day a week back — same weekday, which is the only honest
   * baseline for an auction market. "Seven days ago" would put a Wednesday
   * board against a Tuesday one at barns that run different cattle on
   * different days; CLAUDE.md lists day-of-week misalignment as a bug pattern
   * this product has been burned by. The RPC resolves it; this passes it out so
   * a caller can fetch that day's bands and show the week's move.
   */
  prevDate: string | null;
}

export interface TickerPayload {
  /** "2026-09-11", or null when nothing has sold in the look-back window. */
  day: string | null;
  /** "Friday, September 11" */
  dayLabel: string;
  /** "Friday's sales" / "Today's sales" — see `dayHeadline`. */
  headline: string;
  bands: TickerBand[];
  summary: TickerSummary | null;
  /** ISO timestamp of the newest print on the board, or null. RAW, not
   *  formatted: it is the input to marketStatus(), which needs to subtract it
   *  from the current time. The formatted version lives on each lot. */
  latestAt: string | null;
  /** True when this payload is the empty state rather than a real read, so the
   *  UI can say "no sales posted" instead of rendering seven blank columns. */
  empty: boolean;
  /**
   * Did we actually reach RaisedBeef?
   *
   * ⚠️ WITHOUT THIS THE BOARD LIES ON A BAD SIGNAL. Every read here returns null
   * rather than throwing, and the payload that came back was the same empty one
   * a genuinely quiet day produces — so a phone that could not reach us rendered
   * eight weight bands each reading "No sales". In a barn alley or on a gravel
   * road, which is exactly where this app gets used, it told a man the market
   * was empty when the truth was his phone had no signal.
   *
   * `empty` means nothing sold. `reachable: false` means we do not know.
   */
  reachable: boolean;
}

/** Whether the ring is running, and what to say if it is not. */
export interface MarketStatus {
  open: boolean;
  /** "Live from the ring" / "Market closed" */
  label: string;
  /** "updates every minute" / "Sales resume Monday morning" */
  detail: string;
}

/**
 * The class filter's four tabs, per design/Main.dc.html.
 *
 * ⚠️ COW/BULL SHOWS ONLY THE LIGHT END OF THE CULL TRADE, and that is a data
 * fact rather than a bug. The board is banded 300–999 lb, the platform
 * WEIGHT_CLASSES taxonomy. Measured 2026-09-11 at this tape's own floor
 * (>=10 head, priced):
 *
 *     Steers    94.7% of lots fall inside 300–999
 *     Heifers   90.0%
 *     Bulls     66.7%
 *     Cows       6.9%   (median 1,268 lb)
 *
 * Cull cattle need their OWN bands (~900–1,600 lb), which is a class-aware
 * taxonomy decision to escalate, never a local range to invent inside one tool.
 * The design's real answer is the "Cull cows & bulls →" tile on the board,
 * which points at a surface built for them. Tracked as T1 in NEXT_SESSION.md.
 */
export const CATTLE_VIEWS = [
  { key: "Steers", label: "Steers" },
  { key: "Heifers", label: "Heifers" },
  // 'All' is steers and heifers: the ticker carries no cows or bulls (owner,
  // 2026-10-11: "Let's remove cows and bull filters from the ticker").
  { key: "All", label: "All" },
] as const;

export type CattleView = (typeof CATTLE_VIEWS)[number]["key"];

/**
 * Does this view mix classes?
 *
 * Only then does a class badge carry information — on the Steers tab every row
 * is a steer and the badge is noise on every line. It matters most on All,
 * where the 28-day VWAP spread between the classes present is wide enough that
 * an unlabelled board misleads: Bulls $377.24, Steers $347.38, Heifers $333.48,
 * Cows $278.83 — $98/cwt between the extremes, and $13.90 between steers and
 * heifers alone.
 */
export function viewMixesClasses(view: CattleView): boolean {
  return view === "All";
}

/** One option in the barn picker. */
export interface TickerBarn {
  name: string;
  state: string;
  /** "2,486 hd" */
  headLabel: string;
  head: number;
}

/** Central. The barn's clock, the RPC's day boundary, and the industry quote. */
const TZ = "America/Chicago";

/**
 * The eight bands on the board — one past the shared platform taxonomy.
 *
 * ⚠️ WEIGHT_CLASSES (src/data/constants.ts) is 300–999 and is UNCHANGED, along
 * with its per-class freight math and the published MarketDataSchema. This list
 * belongs to the homepage tape alone; CLAUDE.md's rule is that widening the
 * shared taxonomy is a platform change, so it was escalated and decided rather
 * than quietly assumed (owner, 2026-09-12).
 *
 * 1000–1099 earns the eighth cell on volume. Steers, 28 days, at this tape's
 * own floor (>=10 head, priced):
 *
 *     900–999    28.0 lots/day   37,023 head   $313.84   24 of 24 days
 *     1000–1099  10.6 lots/day   11,797 head   $298.74   21 of 24 days
 *     1100–1199   2.3 lots/day    1,573 head   $271.24   16 of 24 days
 *
 * It trades nearly every day and its VWAP continues the slide from 900
 * sensibly; 1100–1199 would read "No sales" a third of the time. It is also
 * still FEEDER cattle — is_feeder_weight() cuts at 1300 — so the band cannot
 * put fed cattle into a feeder average, which is the whole reason the old 999
 * ceiling existed.
 */
const BANDS = [300, 400, 500, 600, 700, 800, 900, 1000] as const;

export const EMPTY_TICKER: TickerPayload = {
  day: null,
  dayLabel: "",
  headline: "No sales posted yet",
  bands: BANDS.map((band) => ({
    band,
    label: `${band}–${band + 99}`,
    vwapLabel: "—",
    rangeLabel: "",
    depthLabel: "",
    lots: [],
  })),
  summary: null,
  latestAt: null,
  empty: true,
  // The starting value and a quiet day are both REACHED states; only a failed
  // read sets this false.
  reachable: true,
};

// ── Formatting ────────────────────────────────────────────────────────────
// en-US is passed explicitly to every formatter. Left to the ambient locale,
// a build machine and a visitor's browser can disagree on the thousands
// separator, which is a hydration mismatch over a comma.

const num = (n: number): string => n.toLocaleString("en-US");
/** Every dollar figure shows cents, $343.00 not $343 (owner, 2026-09-13). */
const CENTS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/** Plural class in the data -> the singular word a badge shows. Unknown classes
 *  map to "", which renders no badge — better than inventing a label for a
 *  class nobody has audited. */
const CLASS_LABEL: Record<string, string> = {
  Steers: "Steer",
  Heifers: "Heifer",
  Cows: "Cow",
  Bulls: "Bull",
};

/** "$490.00" — cents always, because a tape that shows $490 next to $490.50
 *  reads as two different precisions of the same thing. */
function money(v: unknown): string {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
}

/** "$385.00 – $590.00". Cents always, like every other price on the site (owner, 2026-09-13). */
function range(low: unknown, high: unknown): string {
  const l = Number(low);
  const h = Number(high);
  if (!Number.isFinite(l) || !Number.isFinite(h)) return "";
  return `$${l.toFixed(2)} – $${h.toFixed(2)}`;
}

/** "5:36 PM", Central. See rule 2 in the header. */
export function centralTime(iso: unknown): string {
  if (typeof iso !== "string" || !iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: TZ,
  });
}

/** "Friday, September 11", Central. Noon UTC so the date can never slip a day
 *  when a date-only string is parsed as midnight UTC. */
function longDate(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** "2026-09-01" -> "Sep 1". Noon UTC so the date can never slip a day when a
 *  date-only string is parsed as midnight UTC and rendered in a western zone. */
function shortDate(day: unknown): string | null {
  if (typeof day !== "string" || !day) return null;
  const d = new Date(`${day}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * What the tape calls the day it is showing.
 *
 * NOT simply "Today". The RPC falls back to the most recent day that sold,
 * which on a Sunday evening is Saturday and after a holiday can be three days
 * back. Labelling Saturday's prices "Today" on a Monday morning is the single
 * most damaging thing this page could do — a producer would read a stale board
 * as a live one and price cattle off it.
 *
 * `todayCentral` is passed in rather than read here so the caller controls it:
 * the prerender has no business claiming to know the visitor's today.
 */
export function dayHeadline(day: string | null, todayCentral: string | null): string {
  if (!day) return "No sales posted yet";
  if (todayCentral && day === todayCentral) return "Today's sales";

  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "Latest sales";
  const weekday = d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });

  if (todayCentral) {
    const diff = Math.round(
      (Date.parse(`${todayCentral}T12:00:00Z`) - d.getTime()) / 86_400_000,
    );
    if (diff === 1) return "Yesterday's sales";
  }
  return `${weekday}'s sales`;
}

/** Today in Central, as YYYY-MM-DD. Client-side only — the prerender must pass
 *  null, because the build machine's today is not the visitor's. */
export function todayCentral(): string {
  // en-CA formats as YYYY-MM-DD, which is the shape the RPC speaks.
  return new Date().toLocaleDateString("en-CA", { timeZone: TZ });
}

// ── Transport ─────────────────────────────────────────────────────────────

interface RawLot {
  band: number; cattle_type: string; sale_barn_name: string; state: string;
  head_count: number; avg_weight: number; price_cwt: number;
  created_at: string; report_date: string;
  /** rpc_cattle_ticker_every_lot only. */
  counts?: boolean | null; min_head?: number | null;
}
interface RawBand { band: number; lots: number; head: number; vwap: number; low: number; high: number; }
interface RawSummary {
  report_date: string; barns: number; lots: number; head: number;
  steers: number; heifers: number; cows: number; bulls: number;
  prev_date: string; prev_head: number; typical_head: number; partial: boolean;
}

/**
 * Call one RPC.
 *
 * In the BROWSER this goes through /api/market, the CDN-cached allowlist proxy,
 * so the homepage's traffic is served from the edge and the database sees one
 * query per scope per minute however many people are watching.
 *
 * Under NODE (the build-time prerender) that relative path does not resolve, so
 * it calls Supabase's REST endpoint directly. Same RPC, same arguments, same
 * result — only the route differs.
 */
/**
 * Fetch and parse a JSON array, or null if ANYTHING about the answer is wrong.
 *
 * ⚠️ THE PARSE IS INSIDE THE GUARD, DELIBERATELY. The proxy call above used to
 * be `if (res.ok) return await res.json()`, which honours "fall through on a
 * cache problem" only for a non-200. A 200 carrying something that is not JSON
 * threw out of the whole try block and the caller got null — the blank panel
 * the fall-through exists to prevent. That is not hypothetical: the local dev
 * server answers /api/market with the edge function's own SOURCE, as
 * text/javascript, status 200, which blanked both interior ticker boards while
 * production was fine. In production the same shape arrives as a platform error
 * page or a half-deployed function.
 *
 * A non-array is rejected for the same reason: PostgREST returns an object for
 * an error, and `{message: "..."}` would flow on as data.
 */
async function readJsonArray<T>(url: string, init?: RequestInit): Promise<T[] | null> {
  try {
    const res = await fetch(url, init);
    if (!res.ok) return null;
    const parsed = await res.json();
    return Array.isArray(parsed) ? (parsed as T[]) : null;
  } catch {
    return null;
  }
}

// ── WHO IS ASKING ──────────────────────────────────────────────────────────
//
// ⚠️ THIS MODULE USED TO TELL THE DATABASE "I AM A STRANGER" EVEN WHEN SOMEBODY
// WAS SIGNED IN. 54 files import it, 12 of them gated tools and 14 of them
// Foote's. There was no `getSession`, no access token, nothing. Every request
// went out holding the publishable key.
//
// That was harmless only because every table was open to everyone. The moment
// a policy says "answer only if auth.uid() is not null", every screen fed by
// this module goes blank — INCLUDING screens behind a login, for users who are
// logged in — and it goes blank SILENTLY, because a policy refusal to a
// SECURITY INVOKER function returns zero rows and no error. Measured, and
// written up in `audits/anon-identity-audit-2026-10-03.md`.
//
// ── WHY A SETTER AND NOT AN IMPORT ─────────────────────────────────────────
//
// The obvious fix is to call `supabase.auth.getSession()` here. That is exactly
// what the header of this file forbids, and for a live reason: the shared
// client touches `localStorage` AT MODULE SCOPE, which throws under Node during
// the homepage prerender. Importing it here breaks the build of the company's
// front door.
//
// So the dependency runs the other way. The app shell already watches
// `onAuthStateChange`; it pushes the token DOWN here (see
// `src/lib/tickerAuth.ts`). This module imports nothing new and keeps working
// under Node, where nobody ever calls the setter.
//
// ⚠️ INERT UNTIL SOMETHING CALLS IT. With no token the headers below are
// byte-for-byte what they were before. The prerender, the lobby TV, the barn
// apps and every anonymous visitor take the identical path they took
// yesterday. That is deliberate: this lands as a no-op and the gating that
// gives it a purpose comes later, separately.

let authToken: string | null = null;

/**
 * Hand this module the signed-in user's access token, or `null` when they sign
 * out. Called by `attachTickerAuth()` from the app shells — not from call
 * sites, of which there are 54.
 */
export function setTickerAuthToken(token: string | null): void {
  const changed = (authToken === null) !== (token === null);
  authToken = token;
  // Only signing in or out is news. An hourly refresh swaps the string and
  // changes nobody's access.
  if (changed) authListeners.forEach((fn) => fn());
}

const authListeners = new Set<() => void>();

/**
 * Called whenever the visitor becomes signed in or signed out here.
 *
 * ⚠️ THE FIRST ANSWER CAN BE TOO EARLY (owner, 2026-10-13: the homepage "acts
 * like no one is logged in"). Pages wait two seconds for the session and then
 * ask as a stranger; on a phone whose token has to be renewed first, the
 * session lands after that, and nothing asked again. Returns the unsubscribe.
 */
export function onTickerAuthChange(fn: () => void): () => void {
  authListeners.add(fn);
  return () => { authListeners.delete(fn); };
}

/** The current token, for tests and for the cache decision below. */
export function tickerAuthToken(): string | null {
  return authToken;
}

/**
 * The bearer token to send: the user's if we have one, otherwise the
 * publishable key, which is what an anonymous caller sends.
 *
 * ⚠️ `apikey` STAYS THE PUBLISHABLE KEY EITHER WAY. Supabase routes on `apikey`
 * and authenticates on `Authorization`; sending a user's JWT as `apikey` is
 * rejected at the gateway before any policy is consulted.
 */
function authHeaders(key: string): Record<string, string> {
  return {
    apikey: key,
    Authorization: `Bearer ${authToken ?? key}`,
    "content-type": "application/json",
  };
}

/**
 * May this call use the shared `/api/market` CDN cache?
 *
 * ⚠️ NO, ONCE SOMEBODY IS SIGNED IN — and this is the whole point, not a
 * precaution. `/api/market` is a CDN cache keyed on the function and its
 * declared params; it is SHARED by every visitor and it CANNOT carry an
 * identity. After gating lands, the anonymous answer to a gated function is
 * EMPTY, and that empty is exactly what the cache would hold and then serve to
 * a paying signed-in user. Same silent-blank failure as the per-user bug
 * recorded in `rpc()` below, but harder to see, because the cache would be
 * doing precisely what it was built to do.
 *
 * ⚠️ IT COSTS SPEED, MEASURED NOT GUESSED: cached 41–60ms against direct
 * 107–122ms, so +50–80ms per call for signed-in users. That is the trade the
 * audit named, and it is the right direction — slower and correct beats fast
 * and blank.
 */
function mayUseCache(perUser: boolean): boolean {
  return !perUser && authToken === null && typeof window !== "undefined";
}

/**
 * Call one RPC that returns a single JSON VALUE (a jsonb object, or a scalar
 * like a date) rather than a set of rows — through the same CDN-first path as
 * `rpc`, with the same direct fallback.
 *
 * `accept` decides what counts as a real answer. PostgREST answers an error
 * with an object too, so "it parsed" is not enough: the caller names the shape
 * it needs, and anything else falls through (proxy) or returns null (direct).
 */
export async function rpcValue<T>(
  fn: string,
  params: Record<string, unknown>,
  accept: (v: unknown) => v is T,
): Promise<T | null> {
  const clean: Record<string, string> = {};
  const body: Record<string, unknown> = {};
  // ⚠️ Same rule as rpc() below: an array param is a per-user scope the shared
  // CDN must not serve, and the proxy would silently drop it and answer with
  // the national figure. Kept here too so the two helpers cannot diverge.
  const perUser = Object.values(params).some((v) => Array.isArray(v) && v.length > 0);
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    if (!Array.isArray(v)) clean[k] = String(v);
    body[k] = v;
  }

  // An answer is boxed so that an ACCEPTED JSON null counts as one. The Sale of
  // the Day is null on a day with no card; if that null fell through as a miss,
  // every visitor would go direct to the database on exactly the days the
  // cache is meant to hold (2026-10-16).
  const read = async (url: string, init?: RequestInit): Promise<{ value: T } | null> => {
    try {
      const res = await fetch(url, init);
      if (!res.ok) return null;
      const parsed: unknown = await res.json();
      return accept(parsed) ? { value: parsed } : null;
    } catch {
      return null;
    }
  };

  if (mayUseCache(perUser)) {
    const qs = new URLSearchParams({ fn, ...clean });
    const cached = await read(`/api/market?${qs}`, { headers: { accept: "application/json" } });
    if (cached) return cached.value;
  }

  const url = import.meta.env?.VITE_SUPABASE_URL;
  const key = import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) return null;
  const direct = await read(`${url}/rest/v1/rpc/${fn}`, {
    method: "POST",
    headers: authHeaders(key),
    body: JSON.stringify(body),
  });
  return direct ? direct.value : null;
}

export async function rpc<T>(fn: string, params: Record<string, unknown>): Promise<T[] | null> {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== "" && !Array.isArray(v)) clean[k] = String(v);
  }

  try {
    // ⚠️ A PER-USER SCOPE NEVER TOUCHES THE SHARED CACHE, AND THIS IS NOT AN
    // OPTIMISATION — IT IS THE BUG THAT SHIPPED.
    //
    // /api/market is a CDN cache for reads that are "identical for everyone
    // given the same params", behind a STRICT ALLOWLIST that forwards only
    // declared params. My Barns sends p_barn_slugs, which is not declared and
    // never should be: a man's starred barns are the opposite of user-agnostic,
    // and every distinct list would be its own CDN entry with a hit rate of
    // roughly zero.
    //
    // What made it a silent wrong answer rather than an error: the proxy
    // DROPPED the undeclared param and returned a perfectly valid NATIONAL
    // board — 200, an array, cacheable. `if (cached) return cached` took it,
    // the fall-through never ran, and the toggle lit up over unchanged prices.
    // Owner, 2026-09-20: "When I switch to my barns I don't think the tickers
    // filtered". It had not.
    //
    // Every check I ran went direct to Supabase with curl and passed. The
    // browser does not take that path.
    //
    // Any array param means a scope the cache must not serve. Go direct.
    const perUser = Object.values(params).some((v) => Array.isArray(v) && v.length > 0);

    if (mayUseCache(perUser)) {
      const qs = new URLSearchParams({ fn, ...clean });
      const cached = await readJsonArray<T>(
        `/api/market?${qs}`,
        { headers: { accept: "application/json" } },
      );
      if (cached) return cached;
      // Fall through to the direct call: a cache problem must never be able to
      // blank the homepage's centre panel.
    }

    const url = import.meta.env?.VITE_SUPABASE_URL;
    const key = import.meta.env?.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) return null;

    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined && v !== "") body[k] = v;
    }
    // Same guard on the direct call. PostgREST answers an error with an
    // OBJECT, and `{code, message, hint}` passed off as rows would reach the
    // formatters as one undefined-everything lot.
    return await readJsonArray<T>(`${url}/rest/v1/rpc/${fn}`, {
      method: "POST",
      headers: authHeaders(key),
      body: JSON.stringify(body),
    });
  } catch {
    return null;
  }
}

function buildLot(r: RawLot): TickerLot {
  const head = Number(r.head_count) || 0;
  const weight = Number(r.avg_weight) || 0;
  const price = Number(r.price_cwt);

  return {
    // created_at alone is not unique — a barn can post two lots in the same
    // second — so the identity carries the barn and the weight too.
    id: `${r.sale_barn_name}|${r.created_at}|${weight}|${head}`,
    head,
    headLabel: `${num(head)} hd`,
    weightLabel: `${num(Math.round(weight))} lb`,
    priceLabel: money(price),
    // $/cwt is the quote, but $/head is the number a producer actually banks,
    // and the conversion (price × weight ÷ 100) is the one people get wrong in
    // their head. cwt = hundredweight = 100 lb.
    perHeadLabel: Number.isFinite(price) && weight > 0
      ? `$${((price * weight) / 100).toLocaleString("en-US", {
          minimumFractionDigits: 2, maximumFractionDigits: 2,
        })}`
      : "—",
    timeLabel: centralTime(r.created_at),
    at: String(r.created_at ?? ""),
    barn: String(r.sale_barn_name ?? ""),
    state: String(r.state ?? ""),
    cattleType: String(r.cattle_type ?? ""),
    classLabel: CLASS_LABEL[String(r.cattle_type ?? "")] ?? "",
    counts: r.counts === undefined || r.counts === null ? true : r.counts === true,
    minHead: r.min_head === undefined || r.min_head === null ? null : Number(r.min_head),
  };
}

/**
 * "▲ 76% above a typical Monday" — the receipts pill's words.
 *
 * ⚠️ `vsTypical` is the SIZE of the change, unsigned; `dir` carries the sign.
 * The map used to print "{vsTypical}% of a typical Monday", so a day at 176% of
 * normal read "76% of a typical Monday" — a big day told as a slow one
 * (2026-09-14 launch audit). Direction is said in words AND an arrow.
 */
export function vsTypicalPhrase(vsTypical: string, dir: "up" | "down" | null, weekday: string): string {
  if (dir === "up") return `▲ ${vsTypical}% above a typical ${weekday}`;
  if (dir === "down") return `▼ ${vsTypical}% below a typical ${weekday}`;
  return `About a typical ${weekday}`;
}

function buildSummary(s: RawSummary | undefined): TickerSummary | null {
  if (!s) return null;
  const head = Number(s.head) || 0;
  const typical = Number(s.typical_head);

  // A percentage against a zero or missing baseline is not a number; it is a
  // division by zero rendered as "Infinity%". Withhold it instead.
  const usable = Number.isFinite(typical) && typical > 0 && head > 0;
  const pct = usable ? ((head - typical) / typical) * 100 : null;
  // Under half a percent displays as "0%", and an arrow on a flat reading
  // points somewhere the data does not. Same guard as rbHomeKpis.
  const flat = pct !== null && Math.abs(pct) < 0.5;

  return {
    barns: num(Number(s.barns) || 0),
    lots: num(Number(s.lots) || 0),
    head: num(head),
    mix: [
      { label: "Steers", value: num(Number(s.steers) || 0), head: Number(s.steers) || 0 },
      { label: "Heifers", value: num(Number(s.heifers) || 0), head: Number(s.heifers) || 0 },
      { label: "Cows", value: num(Number(s.cows) || 0), head: Number(s.cows) || 0 },
      { label: "Bulls", value: num(Number(s.bulls) || 0), head: Number(s.bulls) || 0 },
    ],
    vsTypical: pct === null ? null : Math.abs(pct).toFixed(0),
    dir: pct === null || flat ? null : pct > 0 ? "up" : "down",
    partial: s.partial === true,
    prevDate: s.prev_date ? String(s.prev_date).slice(0, 10) : null,
  };
}

/**
 * Reads the tape, the band aggregates and the scoreboard for one scope.
 *
 * The three calls go out together: they are independent reads of the same day,
 * and issuing them in sequence would make the panel three round-trips slow for
 * no reason. They cannot disagree about WHICH day — all three resolve it
 * through rpc_cattle_ticker_day server-side.
 *
 * Never throws. Returns EMPTY_TICKER on any failure.
 */
export async function fetchCattleTicker(
  scope: TickerScope = {},
  today: string | null = null,
): Promise<TickerPayload> {
  // ⚠️ AN EMPTY LIST IS NOT A SCOPE, IT IS NULL. `[]` would reach the RPC as an
  // empty array, resolve to no barn names, and draw an empty board. A man with
  // no barns starred must see the national board, not a blank one.
  const slugs = scope.barnSlugs?.length ? scope.barnSlugs : null;

  const args = {
    p_state: scope.state ?? null,
    p_barn: scope.barn ?? null,
    p_barn_slug: scope.barnSlug ?? null,
    p_barn_slugs: slugs,
    p_cattle_type: scope.cattleType ?? "Steers",
    p_min_head: scope.minHead ?? 10,
  };

  const [rawLots, rawBands, rawSummary, lastPrint] = await Promise.all([
    rpc<RawLot>(scope.everyLot ? "rpc_cattle_ticker_every_lot" : "rpc_cattle_ticker", {
      ...args,
      p_per_band: scope.perBand ?? 5,
      p_sale_date: scope.saleDate ?? null,
    }),
    rpc<RawBand>("rpc_cattle_ticker_bands", {
      ...args,
      p_sale_date: scope.saleDate ?? null,
    }),
    // ⚠️ THE SLUG GOES TO THE SCOREBOARD TOO, AND IT IS NOT OPTIONAL. `day`
    // below is read off THIS call, and every label on the page hangs off it.
    // While this one call was left unscoped, a barn board resolved its lots
    // through the barn's own day and took its DATE from the national one — so
    // the StateLine Stockyards board read "Today's sales · Saturday,
    // September 12" over Thursday's prints, and the sidebar, which queries
    // that date for that barn, read "No lots on this sale day" beside a board
    // full of them. Migration 20260912000004 added the parameter.
    rpc<RawSummary>("rpc_cattle_ticker_summary", {
      p_state: args.p_state,
      p_barn: args.p_barn,
      p_barn_slug: args.p_barn_slug,
      // ⚠️ AND THE LIST GOES HERE TOO, for exactly the reason above it.
      p_barn_slugs: args.p_barn_slugs,
      p_sale_date: scope.saleDate ?? null,
    }),
    // ⚠️ THE LAMP IS NOT THE BOARD (owner, 2026-09-14: "the board says closed
    // today even though barns are live"). The newest lot ON THE BOARD is the
    // newest 10+ head pen of the open tab; Bluegrass South had sold 64 small lots
    // in 45 minutes and the board had none, so the lamp said CLOSED over a live
    // ring. Whether the ring is running is asked of every lot in scope.
    rpc<{ last_print: string | null }>("rpc_cattle_ticker_last_print", {
      p_state: args.p_state,
      p_barn: args.p_barn,
      p_barn_slug: args.p_barn_slug,
      p_barn_slugs: args.p_barn_slugs,
    }),
  ]);

  // ⚠️ COULD WE REACH ANYTHING AT ALL? `rpc` returns null on a network failure
  // and an ARRAY (often empty) on a successful read, so "every read came back
  // null" is the signal that we never got through — as opposed to a real day
  // with nothing in it. Told apart here because the payload for both used to be
  // identical, and the board said "No sales" either way.
  const reachedSomething =
    Array.isArray(rawLots) || Array.isArray(rawBands) || Array.isArray(rawSummary);
  if (!reachedSomething) return { ...EMPTY_TICKER, reachable: false };

  const summary = Array.isArray(rawSummary) ? rawSummary[0] : undefined;
  const day = summary?.report_date ?? (Array.isArray(rawLots) ? rawLots[0]?.report_date : null) ?? null;
  if (!day) return EMPTY_TICKER;

  const bandStats = new Map<number, RawBand>();
  for (const b of rawBands ?? []) bandStats.set(Number(b.band), b);

  const lotsByBand = new Map<number, TickerLot[]>();
  let newest: string | null = null;
  for (const r of rawLots ?? []) {
    const key = Number(r.band);
    const list = lotsByBand.get(key) ?? [];
    list.push(buildLot(r));
    lotsByBand.set(key, list);
    if (r.created_at && (newest === null || r.created_at > newest)) newest = r.created_at;
  }

  return {
    day,
    dayLabel: longDate(day),
    headline: dayHeadline(day, today),
    // We got a day out of a real read, so we plainly got through.
    reachable: true,
    // Always all seven bands, in order, present or not. A band that stops
    // existing because nothing sold in it would make the board's columns jump
    // between refreshes, and an empty 300–399 is itself information on a day
    // when no calves ran.
    bands: BANDS.map((band) => {
      const stat = bandStats.get(band);
      const lots = lotsByBand.get(band) ?? [];
      return {
        band,
        label: `${band}–${band + 99}`,
        vwapLabel: stat ? money(stat.vwap) : "—",
        rangeLabel: stat ? range(stat.low, stat.high) : "",
        depthLabel: stat
          ? `${num(Number(stat.lots) || 0)} ${Number(stat.lots) === 1 ? "lot" : "lots"} · ` +
            `${num(Number(stat.head) || 0)} hd`
          : "",
        headLabel: stat ? `${num(Number(stat.head) || 0)} hd` : "",
        lots,
        // The same figures raw, for the chart and the range bar. See TickerBand.
        vwap: stat && Number.isFinite(Number(stat.vwap)) ? Number(stat.vwap) : null,
        low: stat && Number.isFinite(Number(stat.low)) ? Number(stat.low) : null,
        high: stat && Number.isFinite(Number(stat.high)) ? Number(stat.high) : null,
        head: stat ? Number(stat.head) || 0 : 0,
        lotCount: stat ? Number(stat.lots) || 0 : 0,
      };
    }),
    summary: buildSummary(summary),
    // The later of the board's newest lot and the scope's newest print of any
    // class or size — see rpc_cattle_ticker_last_print above.
    latestAt: laterOf(newest, Array.isArray(lastPrint) ? (lastPrint[0]?.last_print ?? null) : null),
    empty: !(rawLots?.length ?? 0),
  };
}

/** The later of two ISO timestamps, either of which may be missing. */
function laterOf(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return Date.parse(b) > Date.parse(a) ? b : a;
}

/** A scoped board plus the head floor it actually used. */
export interface ScopedTicker {
  payload: TickerPayload;
  /** 10 normally; 1 when nothing at 10+ head sold in this scope on this day. */
  minHead: number;
}

/**
 * A board for ONE barn or ONE state, with the head floor adapted to the scope.
 *
 * ── Why the national floor cannot be used unchanged ───────────────────────
 * The homepage requires 10+ head, and that is right for a national board: a
 * single-head price is not a market print, and on a national tape it is noise
 * beside 60-head pens. At a state or barn scope the SAME floor deletes entire
 * markets, because plenty of barns trade almost nothing but small lots.
 * Measured on production, 2026-09-12, on each state's own latest sale day:
 *
 *   TN  126 lots  141 hd  avg lot 1.1 hd   → 0 lots at a 10-head floor
 *   NC  106 lots  120 hd  avg lot 1.1 hd   → 0
 *   AR   77 lots   94 hd  avg lot 1.2 hd   → 0
 *   CA   50 lots   83 hd  avg lot 1.7 hd   → 0
 *   NM, OR, IN, IA, NV, AL — the same shape
 *
 * Eleven states would have rendered eight empty bands while a real sale ran.
 * A Tennessee cow-calf man would open his own state's board, see "No sales" in
 * every band, and be right to conclude the product is broken.
 *
 * So: try 10+ first, and fall back to every lot size ONLY when 10+ returns an
 * empty board. The caller gets the floor back and MUST say which one is on
 * screen — a 2-head print and a 60-head pen are not the same number, and a
 * board that silently mixed the two would be an undisclosed editorial choice.
 * If both floors come back empty the scope genuinely had no qualifying sale,
 * so 10 is reported and the board reads "No sales", which is the truth.
 *
 * The extra round trip happens only in the fallback case, and never on the
 * homepage — this function is for the barn and state pages.
 */
export async function fetchScopedTicker(
  scope: TickerScope = {},
  today: string | null = null,
): Promise<ScopedTicker> {
  // ⚠️ THE TICKER IS STEERS AND HEIFERS ONLY (owner, 2026-10-11: "Let's
  // remove cows and bull filters from the ticker"). The Cow/Bull tab and its
  // every-lot mode are gone, and the database returns no cows or bulls to a
  // ticker request. Lot sizes are the database's rb_lot_counts rule, whatever
  // minHead is passed: 5+ head under 500 lb, 10+ at 500 lb and over.
  const pens = await fetchCattleTicker({ ...scope, minHead: 10 }, today);
  if (!boardIsEmpty(pens)) return { payload: pens, minHead: 10 };

  const small = await fetchCattleTicker({ ...scope, minHead: 1 }, today);
  return boardIsEmpty(small) ? { payload: pens, minHead: 10 } : { payload: small, minHead: 1 };
}

/** Every band present but not one lot printed. `empty` on the payload says the
 *  same thing; this reads the bands so it stays true if that ever changes. */
function boardIsEmpty(p: TickerPayload): boolean {
  return p.bands.every((b) => b.lots.length === 0);
}

/**
 * Barns that sold on the day being shown, heaviest first.
 *
 * Separate from fetchCattleTicker because it changes on a different clock:
 * which barns sold is settled by mid-morning, where the tape changes with each
 * print. Fetching it alongside every tape refresh would re-request a list that
 * had not moved. Never throws; an empty list simply leaves the picker offering
 * "United States" only.
 */
export async function fetchTickerBarns(state?: string | null): Promise<TickerBarn[]> {
  const rows = await rpc<{ sale_barn_name: string; state: string; head: number }>(
    "rpc_cattle_ticker_barns",
    { p_state: state ?? null },
  );
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((r) => r?.sale_barn_name)
    .map((r) => {
      const head = Number(r.head) || 0;
      return {
        name: String(r.sale_barn_name),
        state: String(r.state ?? ""),
        head,
        headLabel: `${num(head)} hd`,
      };
    });
}

/**
 * Is the ring running?
 *
 * ── Why this is measured, not scheduled ───────────────────────────────────
 * The obvious implementation is a timetable — "open 8am–4pm Central, closed
 * Sunday". It would be wrong constantly: barns run to their own hours, a big
 * consignment pushes a sale into the evening, holidays close the whole country,
 * and the fall run has barns selling days they normally would not. A timetable
 * says "open" on Thanksgiving.
 *
 * So this asks the only question that is actually true: HAS ANYTHING SOLD
 * RECENTLY. Silence is the signal.
 *
 * ── Why 90 minutes ───────────────────────────────────────────────────────
 * Measured over four weeks of active selling hours (9am–3pm CT, 127,788
 * intervals): the 99th-percentile gap between prints is 0.6 minutes and the
 * 99.9th is 1.5. Ninety minutes is sixty times the quietest normal moment, so
 * a false "closed" during a live sale is effectively impossible, while the
 * board still stops claiming to be live within about an hour and a half of the
 * last barn finishing.
 *
 * ⚠️ CLIENT-SIDE ONLY. This depends on the current time, which the build
 * machine does not share with the visitor — calling it during the prerender
 * would bake one answer into a document served for days. TickerBoard resolves
 * it in an effect, after mount, and renders a neutral state until then.
 */
export function marketStatus(latestAt: string | null, now: Date = new Date()): MarketStatus {
  const LIVE_WINDOW_MS = 90 * 60 * 1000;

  const last = latestAt ? Date.parse(latestAt) : NaN;
  if (Number.isFinite(last) && now.getTime() - last < LIVE_WINDOW_MS) {
    return { open: true, label: "Live from the ring", detail: "updates every minute" };
  }
  return { open: false, label: "Market closed", detail: `Sales resume ${nextSellingDay(now)}` };
}

/**
 * When cattle will sell next.
 *
 * ── The mistake this replaced ────────────────────────────────────────────
 * The first version asked only "what day is it" and answered Saturday with
 * "Monday morning". Caught on a simulator at 1:27am on a Saturday: Saturday's
 * sale had not happened YET, and the board was telling a reader the market was
 * shut for two days when it opened in a few hours. The day of the week is not
 * the question — whether TODAY'S selling window has already passed is.
 *
 * Sunday is the only day the country genuinely does not sell: across eight
 * weeks Sundays carried ONE head in total, against Saturday's 17 barns and
 * ~4,300 head every single week. So Saturday is a normal (thin) sale day and
 * only Sunday is skipped.
 *
 * Deliberately vague about the hour. Monday's first print has landed anywhere
 * between 3:32am and 7:53am Central over nine weeks, and Saturday's averages
 * ~3:48am — most of the early ones are overnight backfill rather than a live
 * ring, so "morning" is the most precise thing that is reliably true.
 * Promising a clock time would be a promise the data does not support.
 */
function nextSellingDay(now: Date): string {
  const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // The BARN'S day and hour, not the visitor's. Someone reading at 11pm
  // Pacific on Saturday is already into Sunday in Central and must be told
  // Monday; someone reading at 1am Central on Saturday must be told today.
  const central = new Date(now.toLocaleString("en-US", { timeZone: TZ }));
  const dow = central.getDay(); // 0 = Sunday
  const hour = central.getHours();

  // Still inside today's selling window, and today sells. Sales run from early
  // morning to roughly 9pm Central, so before 8pm the honest answer is that
  // today's are still to come — whether they have not started or are in a lull.
  const SELLING_ENDS_HOUR = 20;
  if (dow !== 0 && hour < SELLING_ENDS_HOUR) {
    return hour < 11 ? "this morning" : "later today";
  }

  // Today is done (or it is Sunday). Walk forward to the next day that sells.
  let next = (dow + 1) % 7;
  if (next === 0) next = 1; // skip Sunday
  return `${names[next]} morning`;
}

/**
 * Every printed lot on the board, in arrival order — newest first.
 *
 * The board groups by weight band because that is how a buyer reads a market.
 * The crawling tape is the opposite view: what the ring actually sounded like,
 * in the order it happened, regardless of weight. Both come from the same
 * payload so they can never disagree about what sold.
 */
export function tapeLots(payload: TickerPayload): TickerLot[] {
  const all: TickerLot[] = [];
  for (const b of payload.bands) all.push(...b.lots);
  // Descending by raw timestamp. `at` exists for exactly this.
  return all.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}


// ── Packer margin ───────────────────────────────────────────────────────────

/** The headline packer margin, pre-formatted for the rail card. */
export interface PackerMargin {
  /** "$343" */
  perHead: string;
  /** "$36.32" */
  perCwt: string;
  /** "Positive" | "Thin" | "Negative" */
  condition: string;
  /** "positive" | "thin" | "negative" — for styling. */
  tone: "positive" | "thin" | "negative";
  /** Week-on-week change in $/head, unsigned; null when unavailable. */
  vsWeek: string | null;
  dir: "up" | "down" | null;
  /** "Sep 1" — the day the CASH leg is from. See below; this is not optional. */
  cashAsOf: string | null;
  /** True when the byproduct credit is absent, so the figure is GROSS. */
  gross: boolean;
}

/**
 * Reads the packer margin.
 *
 * ── Why the card exists, and the line it must not cross ─────────────────
 * Packers are not this product's audience. This is an UPSTREAM SIGNAL: a packer
 * working on $343/head has room to bid up, one working on $40 does not, and
 * that is what a seller wants off this card. Never a packer's book.
 *
 * ── Why cashAsOf ships with the figure, always ──────────────────────────
 * The cash leg is the WEEKLY comprehensive, held flat for 5–8 days while the
 * cutout moves twice daily. So intra-week movement is entirely cutout-driven.
 * Over the three days to 2026-09-11 the margin read $389 → $367 → $343, which
 * looks like a collapse and is not: week over week it was $344 → $343, flat.
 * That is also why the comparison here is WEEKLY.
 *
 * Never throws.
 */
export async function fetchPackerMargin(): Promise<PackerMargin | null> {
  const rows = await rpc<{
    net_margin_per_head: number; net_margin_cwt: number; gross_margin_cwt: number;
    margin_condition: string; cash_as_of: string; drop_credit_missing: boolean;
    prev_week_per_head: number | null;
  }>("rpc_packer_margin_headline", {});
  const r = Array.isArray(rows) ? rows[0] : null;
  if (!r) return null;

  // `Number(null)` is 0, which is finite — so a null margin would have sailed
  // through a Number.isFinite check and rendered as a genuine $0 margin, which
  // is a very different claim from "we do not know". Null must be rejected
  // before the coercion, not after. A real 0 is still allowed through: a
  // break-even packer is meaningful, and this card exists to say so.
  const numOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const head = numOrNull(r.net_margin_per_head);
  const cwt = numOrNull(r.net_margin_cwt);
  if (head === null || cwt === null) return null;

  const prev = numOrNull(r.prev_week_per_head);
  const delta = prev === null ? null : head - prev;
  // Flat below 1% of the level, floored at a dollar.
  //
  // A fixed threshold is wrong on a figure that ranges from break-even to
  // $400/head: $5 is nothing at $343 and everything at $22. One percent scales
  // with the number, so the arrow means "material" at either end — and the
  // dollar floor stops a near-zero margin from arrowing on rounding noise.
  // $344 -> $343 is -$1 on $343: an arrow there is not a market signal.
  const flat = delta !== null && Math.abs(delta) < Math.max(1, Math.abs(head) * 0.01);

  const cond = String(r.margin_condition ?? "").toUpperCase();
  const tone = cond === "POSITIVE" ? "positive" : cond === "THIN" ? "thin" : "negative";

  return {
    perHead: `$${head.toLocaleString("en-US", CENTS)}`,
    perCwt: money(cwt),
    condition: cond ? cond.charAt(0) + cond.slice(1).toLowerCase() : "—",
    tone,
    vsWeek: delta === null ? null : `$${Math.abs(delta).toLocaleString("en-US", CENTS)}`,
    dir: delta === null || flat ? null : delta > 0 ? "up" : "down",
    cashAsOf: shortDate(r.cash_as_of),
    gross: r.drop_credit_missing === true,
  };
}


// ── Fed cattle + boxed beef headlines ──────────────────────────────────────

/** Signed change, pre-formatted with its direction. */
export interface Move {
  /** "$1.27" / "2.43" — UNSIGNED; `dir` carries the sign. */
  value: string;
  dir: "up" | "down" | null;
}

/**
 * now − then, or null when either side is missing.
 *
 * ⚠️ `Number(null)` is 0 AND FINITE, so a null week-ago level would come out
 * as a delta equal to the whole price — a $375 "week over week" on a cutout
 * that did not move. Both sides are rejected before coercion.
 */
function delta(now: unknown, then: unknown): number | null {
  if (now == null || then == null) return null;
  const a = Number(now);
  const b = Number(then);
  return Number.isFinite(a) && Number.isFinite(b) ? a - b : null;
}

function move(delta: number | null, flatUnder = 0.01): Move | null {
  if (delta === null || !Number.isFinite(delta)) return null;
  const flat = Math.abs(delta) < flatUnder;
  return {
    value: Math.abs(delta).toFixed(2),
    dir: flat ? null : delta > 0 ? "up" : "down",
  };
}

export interface FiveArea {
  /** "Sep 7" — the week the weighted average covers. */
  weekOf: string | null;
  /** "$219.07" live, "$344.95" dressed. */
  live: string;
  dressed: string;
  /** "31,492" — the sample behind the weekly figure. */
  head: string;
  /** Week-on-week move in the live price. */
  vsWeek: Move | null;
  /** Week-on-week in the DRESSED price. It printed with nothing beside it
   *  while live carried a change, which reads as "dressed did not move". */
  vsWeekDressed: Move | null;
  /** Today's daily, ONLY when its sample is material. Null otherwise — see
   *  the note on fetchFiveArea. */
  today: { price: string; head: string; date: string | null } | null;
}

/**
 * 5-Area fed cattle.
 *
 * ── Weekly leads, and why ────────────────────────────────────────────────
 * The daily price is steady; its SAMPLE is not. Over three weeks to
 * 2026-09-11 the daily ranged just $217.88–$225.85 while the head confirmed
 * ranged from 68 to 21,452 — so on a thin day the "Five Area price" is one pen
 * quoted as a national market. The weekly aggregates the full week's confirmed
 * trade, which is the figure the industry treats as of record.
 *
 * `today` is populated ONLY when the daily sample clears 5,000 head, because a
 * mid-week break is exactly what the weekly cannot show until the following
 * Monday — and a 105-head day is exactly what should not be on the front door.
 *
 * ⚠️ This is the 5-Area NEGOTIATED series. It is NOT the National Weekly
 * Comprehensive behind the packer-margin cash leg, which reads ~$18/cwt higher
 * because it includes formula and contract cattle. The cards label both.
 *
 * Never throws.
 */
export async function fetchFiveArea(): Promise<FiveArea | null> {
  const rows = await rpc<{
    week_of: string; live_price: number; live_head: number; dressed_price: number;
    prev_week_live: number | null; prev_week_dressed: number | null;
    daily_date: string; daily_price: number;
    daily_head: number; daily_is_material: boolean;
  }>("rpc_five_area_headline", {});
  const r = Array.isArray(rows) ? rows[0] : null;
  if (!r) return null;

  const live = Number(r.live_price);
  if (!Number.isFinite(live)) return null;
  const prev = r.prev_week_live == null ? null : Number(r.prev_week_live);

  return {
    weekOf: shortDate(r.week_of),
    live: money(live),
    dressed: money(r.dressed_price),
    head: num(Number(r.live_head) || 0),
    vsWeek: move(prev === null || !Number.isFinite(prev) ? null : live - prev),
    vsWeekDressed: move(delta(r.dressed_price, r.prev_week_dressed)),
    today: r.daily_is_material === true
      ? {
          price: money(r.daily_price),
          head: num(Number(r.daily_head) || 0),
          date: shortDate(r.daily_date),
        }
      : null,
  };
}

export interface BoxedBeef {
  choice: string;
  select: string;
  /** Day-over-day, from the source's own field — not recomputed. */
  choiceMove: Move | null;
  selectMove: Move | null;
  /** Week-over-week, against the SAME session seven days back. Null when that
   *  day did not publish — a holiday — rather than reaching for a nearer row
   *  and calling six days a week. */
  choiceWeek: Move | null;
  selectWeek: Move | null;
  /** "AM" | "PM" — which session this is. Labelled on the card, because an
   *  unlabelled AM invites comparison with yesterday's PM. */
  session: string;
  /** "Sep 11" */
  asOf: string | null;
}

/**
 * Boxed beef cutout — the FRESHEST session, with the day-over-day change.
 *
 * The homepage previously read get_boxed_beef_primals, which hardcodes
 * report_type = 'PM'. That is right for its cut-level sections (the AM cuts
 * write path is disabled — the PDF parser was corrupting item_description) but
 * it meant that from the ~9:30am CT AM publication to the ~2:45pm PM the card
 * showed YESTERDAY's PM while today's AM sat in the table. On 2026-09-09 that
 * was 382.18 AM against 380.77 PM.
 *
 * The change matters as much as the level, and the two sides can disagree:
 * 2026-09-11 was Choice -2.43 with Select +1.07 — the Choice-Select spread
 * narrowing, which the levels alone hide completely.
 *
 * Never throws.
 */
export async function fetchBoxedBeef(): Promise<BoxedBeef | null> {
  const rows = await rpc<{
    report_date: string; session: string; choice_cutout: number;
    select_cutout: number; choice_change: number | null; select_change: number | null;
    choice_week_ago: number | null; select_week_ago: number | null;
  }>("rpc_boxed_beef_headline", {});
  const r = Array.isArray(rows) ? rows[0] : null;
  if (!r) return null;

  const choice = Number(r.choice_cutout);
  if (!Number.isFinite(choice)) return null;

  return {
    choice: money(choice),
    select: money(r.select_cutout),
    choiceMove: move(r.choice_change == null ? null : Number(r.choice_change)),
    selectMove: move(r.select_change == null ? null : Number(r.select_change)),
    // Computed here rather than server-side because the RPC returns the
    // week-ago LEVEL, not the delta — a level is checkable against the source
    // and a delta is not.
    choiceWeek: move(delta(choice, r.choice_week_ago)),
    selectWeek: move(delta(r.select_cutout, r.select_week_ago)),
    session: String(r.session ?? "").toUpperCase() === "AM" ? "AM" : "PM",
    asOf: shortDate(r.report_date),
  };
}


/** The three figures on THE PACKER SIDE rail, fetched together. */
export interface RailPayload {
  boxed: BoxedBeef | null;
  fiveArea: FiveArea | null;
  margin: PackerMargin | null;
}

export const EMPTY_RAIL: RailPayload = { boxed: null, fiveArea: null, margin: null };

/**
 * All three rail figures in one pass.
 *
 * Fetched together because they are one visual group and three sequential
 * round-trips would make the rail fill in piecemeal. Shared by the build-time
 * prerender and the client refresh for the same reason every other figure on
 * this page is: baking them in puts the numbers in the HTML Google reads, and
 * stops three cards each growing ~70px a beat after first paint and shoving the
 * rail around. Never throws — a null card renders its title and blurb and no
 * figures, which is the honest empty state.
 */
export async function fetchRail(): Promise<RailPayload> {
  const [boxed, fiveArea, margin] = await Promise.all([
    fetchBoxedBeef(),
    fetchFiveArea(),
    fetchPackerMargin(),
  ]);
  return { boxed, fiveArea, margin };
}


// ── Adapter for the other ticker surfaces ──────────────────────────────────

/**
 * The loosest raw lot shape the three surfaces have in common.
 *
 * The homepage reads rpc_cattle_ticker (band, sale_barn_name, state); the barn
 * and state tickers read dv_sale_market_reports through their own queries and
 * carry `weight_class` instead of `band` and no barn on a single-barn board.
 * Both are accepted, so neither page has to reshape its data before it can use
 * the shared board.
 */
export interface RawTickerLot {
  band?: number | null;
  weight_class?: number | null;
  cattle_type?: string | null;
  sale_barn_name?: string | null;
  state?: string | null;
  head_count?: number | null;
  avg_weight?: number | null;
  price_cwt?: number | null;
  created_at?: string | null;
  report_date?: string | null;
}

/**
 * Raw rows -> the eight formatted bands the shared board renders.
 *
 * ⚠️ EVERY DISPLAYED STRING IS FORMATTED HERE, for all three surfaces. That is
 * the point of routing them through one function rather than letting each page
 * format its own: the homepage is prerendered under Node in UTC and hydrated in
 * a rancher's browser, so any formatting that consults the ambient locale or
 * timezone produces two different strings and fails hydration. The barn and
 * state pages do not have that constraint — but if they format independently,
 * the same lot reads differently on two pages of the same product, which is its
 * own kind of wrong.
 *
 * `perBand` caps each band the way rpc_cattle_ticker's p_per_band does, so a
 * page that fetches a whole sale day still renders the same board.
 */
export function toBands(rows: RawTickerLot[], perBand = 5): TickerBand[] {
  const byBand = new Map<number, TickerLot[]>();

  for (const r of rows) {
    const weight = Number(r.avg_weight);
    if (!Number.isFinite(weight)) continue;
    // Prefer an explicit band, else derive it the same way the RPC does.
    const raw = r.band ?? r.weight_class;
    const band = Number.isFinite(Number(raw))
      ? Math.floor(Number(raw) / 100) * 100
      : Math.floor(weight / 100) * 100;
    if (!BANDS.includes(band as (typeof BANDS)[number])) continue;

    const list = byBand.get(band) ?? [];
    list.push(
      buildLot({
        band,
        cattle_type: String(r.cattle_type ?? ""),
        sale_barn_name: String(r.sale_barn_name ?? ""),
        state: String(r.state ?? ""),
        head_count: Number(r.head_count) || 0,
        avg_weight: weight,
        price_cwt: Number(r.price_cwt),
        created_at: String(r.created_at ?? ""),
        report_date: String(r.report_date ?? ""),
      }),
    );
    byBand.set(band, list);
  }

  return BANDS.map((band) => {
    // Newest first, then capped — the same order and depth the board shows
    // everywhere else.
    const lots = (byBand.get(band) ?? [])
      .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
      .slice(0, perBand);

    const head = lots.reduce((n, l) => n + l.head, 0);
    return {
      band,
      label: `${band}–${band + 99}`,
      // Deliberately NOT a VWAP of these lots. The board's band header shows
      // the whole band's weighted average (rpc_cattle_ticker_bands); averaging
      // the five printed lots would be twenty minutes of sample wearing the
      // label of the day. A caller with the real aggregate passes it in; one
      // without shows a dash rather than a number that is almost right.
      vwapLabel: "—",
      rangeLabel: "",
      depthLabel: lots.length
        ? `${lots.length} ${lots.length === 1 ? "lot" : "lots"} · ${head.toLocaleString("en-US")} hd`
        : "",
      lots,
    };
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// THE DIRECTORY — /barns and /states
//
// Both pages are linked from the Cattle Ticker masthead, and until now neither
// route existed: they fell through to the catch-all NotFound. Two of the five
// nav items on the front door were dead, and the 258 barn boards and 33 state
// boards had no hub linking them, so a crawler that reached one had no path to
// the rest.
//
// Read through rpc_ticker_barn_index / rpc_ticker_state_index rather than
// v_sale_barns, for a correctness reason before a performance one: that view
// groups by sale_barn_name, so a barn with two spellings appears TWICE with
// its lots split between the rows. StateLine Stockyards would have listed
// twice, at 769 and 177 lots, instead of once at 946.
// ═══════════════════════════════════════════════════════════════════════════

/** One barn on the /barns directory, entirely pre-formatted. */
export interface IndexBarn {
  slug: string;
  name: string;
  /** Two-letter alpha, for grouping and for the badge. */
  state: string;
  /** "Vinita, OK" — or just the state when the city is unknown. */
  place: string;
  /** Raw, for sorting and for callers that need the value rather than the
   *  label — resolveBarnSlug builds a DirectoryBarn out of these. */
  head: number;
  lots: number;
  /** Raw ISO, alongside the formatted `lastSaleLabel`. */
  lastSaleDate: string | null;
  lotsLabel: string;
  headLabel: string;
  /** "Sep 10" — Central, explicit, like every other date in this module. */
  lastSaleLabel: string;
  href: string;
}

/** One state on the /states directory. */
export interface IndexState {
  alpha: string;
  /** "South Dakota" */
  name: string;
  slug: string;
  head: number;
  barns: number;
  barnsLabel: string;
  lotsLabel: string;
  headLabel: string;
  lastSaleLabel: string;
  href: string;
}

/** alpha → name, built once from the platform's own single source of truth
 *  (src/lib/regions.ts) rather than a second list that could drift from it. */
const ALPHA_TO_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_NAME_TO_ALPHA).map(([name, alpha]) => [alpha, name]),
);

/** "South Dakota" → "south-dakota", matching resolveStateSlug. */
const slugOfState = (name: string): string => name.toLowerCase().replace(/\s+/g, "-");

/**
 * "Joplin Regional Stockyards, MO" — the barn as a board row names it.
 *
 * ⚠️ THE STATE IS NOT ALWAYS OURS TO ADD. DVAuction writes some barn names with
 * the state already inside them, and appending ours doubles it on screen:
 * "Coffeyville Stockyards, LLC - KS (North Town), KS" (owner's phone,
 * 2026-09-16). Others carry a town in parentheses for the same reason, so the
 * name cannot simply be trimmed at the first comma either.
 *
 * So: append the state only when the name does not already say it, matching it
 * as a WHOLE WORD. A substring test would swallow the state in "Kansas" or in
 * "Mt. Hope Auction - Cattle Sale" for OH, and drop a state a reader needs.
 */
export function barnWithState(barn: string | null | undefined, state: string | null | undefined): string {
  const name = String(barn ?? "").trim();
  const st = String(state ?? "").trim();
  if (!name) return st;
  if (!st) return name;
  const already = new RegExp(`(^|[^A-Za-z])${st}([^A-Za-z]|$)`, "i").test(name);
  return already ? name : `${name}, ${st}`;
}

/** A band's weighted average on some earlier day: band → $/cwt. */
export type BandCompare = Map<number, number>;

/**
 * Last week's weighted average for every band, for the move on the app's board.
 *
 * ⚠️ THE SAME WEEKDAY, NOT "SEVEN DAYS AGO", and `day` must come from
 * `summary.prevDate` rather than from date arithmetic in a component. Barns run
 * different cattle on different days — a Wednesday feeder run against a Tuesday
 * pairs-and-bred sale is not a week's move, it is two different markets. The RPC
 * resolves the comparable day; this reads the bands for it. CLAUDE.md lists
 * day-of-week misalignment among the bug patterns this product has been burned by.
 *
 * ⚠️ IT IS ALSO A SEPARATE CALL ON PURPOSE. Folding it into fetchCattleTicker
 * would add a fifth round trip to the homepage, the barn boards and the state
 * boards — every one of which is prerendered and none of which shows a move.
 * The app asks for it; nothing else pays for it.
 *
 * Never throws. Returns an empty map on any failure, and the board then shows
 * no move rather than a wrong one.
 */
export async function fetchBandCompare(
  scope: TickerScope = {},
  day: string | null,
): Promise<BandCompare> {
  const out: BandCompare = new Map();
  if (!day) return out;

  // Key order matches fetchCattleTicker's bands call, so the two share a cache
  // key shape at the /api/market proxy.
  //
  // ⚠️ THE BARN LIST BELONGS HERE TOO. This is the "vs last week" arrow. Scope
  // the board to a man's own barns and leave this national, and the arrow would
  // compare HIS barns this week against EVERY barn in the country last week —
  // a green arrow that means nothing, on the one number a man reads fastest.
  const rows = await rpc<RawBand>("rpc_cattle_ticker_bands", {
    p_state: scope.state ?? null,
    p_barn: scope.barn ?? null,
    p_barn_slug: scope.barnSlug ?? null,
    p_barn_slugs: scope.barnSlugs?.length ? scope.barnSlugs : null,
    p_cattle_type: scope.cattleType ?? "Steers",
    p_min_head: scope.minHead ?? 10,
    p_sale_date: day,
  });
  if (!Array.isArray(rows)) return out;

  for (const r of rows) {
    const band = Number(r?.band);
    const vwap = Number(r?.vwap);
    if (Number.isFinite(band) && Number.isFinite(vwap) && vwap > 0) out.set(band, vwap);
  }
  return out;
}

/**
 * The week's move on a band: "+$5.47" / "−$14.96", and which way.
 *
 * ⚠️ GREEN IS UP AND RED IS DOWN — the price, not the mood (owner's standing
 * rule). A seller reading a red number must be able to take it as "the market
 * came off", every time, on every surface.
 *
 * Dollars and not a percentage: this audience quotes and negotiates in $/cwt,
 * and "$414.77, off $14.96" is the sentence a man says at the coffee shop.
 * Under a dime is called flat rather than dressed up with an arrow — the board
 * is a live tape and a few cents of drift is not news.
 */
export function bandMove(
  now: number | null | undefined,
  then: number | null | undefined,
): { label: string; dir: "up" | "down" | null } | null {
  const a = Number(now);
  const b = Number(then);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;

  const d = a - b;
  if (Math.abs(d) < 0.1) return { label: "Flat", dir: null };
  // U+2212 MINUS, not a hyphen: beside a dollar figure a hyphen reads as a dash
  // in the sentence rather than as a negative number.
  return {
    label: `${d > 0 ? "+" : "−"}${money(Math.abs(d))}`,
    dir: d > 0 ? "up" : "down",
  };
}

/**
 * "Last Wednesday" — the comparable day, said the way a person says it.
 *
 * The chart's legend and the move both lean on this. "Prior period" is what a
 * spreadsheet calls it; a man in an alley says last Wednesday, and being able to
 * name the day is also what makes the same-weekday comparison legible rather
 * than a claim the reader has to take on faith.
 *
 * Parsed at noon UTC and formatted in UTC, like every other date-only value
 * here: a bare "2026-09-09" read in a browser west of Greenwich becomes the 8th,
 * and the weekday would be wrong by one.
 */
export function lastWeekdayLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return `Last ${d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" })}`;
}

/**
 * "vs last Wed" — what a move is measured against, short enough to ride on the
 * row beside it.
 *
 * ⚠️ EVERY CHANGE FIGURE ON A BOARD MUST NAME ITS BASELINE. Owner, 2026-09-16,
 * looking at a bare "−$14.96": "is the - red number day over day or what?",
 * then "but we need to show that." The two readings are worlds apart and the
 * reader cannot tell them apart by looking. Measured that day, of the 24 barns
 * that sold 500–599 lb steers, ONE had also sold the day before and NINE had
 * sold the same weekday a week back — so a day-over-day figure would set 24
 * barns against a nearly different 24 and call the difference the market.
 */
export function vsWeekdayLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return `vs last ${d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" })}`;
}

/** "Sep 10", Central. Short because it sits in a dense list, and explicit
 *  because every other date in this module is. */
function shortDay(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Every reporting barn, alphabetical. Never throws; returns [] on failure. */
/**
 * Directory slugs of the barns selling RIGHT NOW — a lot within 90 minutes, the
 * barn ticker lamp's own rule (rpc_cattle_ticker_live_barns). `now` is
 * injectable for tests; a row the CDN kept past its 90 minutes is dropped here
 * rather than shown as live.
 */
export async function fetchLiveBarnSlugs(now: number = Date.now()): Promise<Set<string>> {
  const rows = await rpc<{ slug: string; last_print: string | null }>("rpc_cattle_ticker_live_barns", {});
  const live = new Set<string>();
  for (const r of rows ?? []) {
    const at = r?.last_print ? Date.parse(r.last_print) : NaN;
    if (r?.slug && Number.isFinite(at) && now - at <= 90 * 60_000) live.add(String(r.slug));
  }
  return live;
}

/** One weekday a barn usually sells on. */
export interface SaleDay {
  slug: string;
  name: string;
  state: string;
  /** 0 = Sunday … 6 = Saturday, as Postgres extract(dow) returns it. */
  dow: number;
  /** Sale days seen on this weekday in the window — the evidence, not a score. */
  saleDays: number;
  /** MEDIAN head on those days. A mean would let one special sale distort it. */
  typicalHead: number;
  lastSold: string | null;
}

/**
 * Which weekdays each barn usually sells on.
 *
 * ⚠️ THIS IS INFERRED, NOT PUBLISHED, AND THE WORDING ON SCREEN MUST SAY SO.
 * Nothing in the database holds a barn's sale calendar — every column matching
 * schedule|sale_day|next_sale|weekday is a report-release calendar or the
 * tool-of-the-day table. This is read back out of sale history, and measured
 * walk-forward over the following fortnight it runs about 90% precision and
 * 90% recall: roughly ONE DAY IN TEN that it names is a day the barn did not
 * sell. So a caller says "usually sells Tuesday", never "Sale Tuesday".
 *
 * ⚠️ IT IS BLIND TO SPECIAL SALES BY DESIGN. A weekday has to recur to count,
 * so a monthly bred-cow or feeder special is dropped. Checked against
 * DVAuction's published schedule (294 sellers, a month ahead): of the 45
 * biggest barns, 29 matched, 20 weekday sets were IDENTICAL, 8 overlapped and
 * none conflicted — and where they differed, THEY had the extra day. That is
 * the special sale. Do not try to catch it by loosening the threshold; that
 * trades a missed special for a false promise, and the false promise is the one
 * somebody drives to.
 */
export async function fetchSaleSchedule(state?: string | null): Promise<SaleDay[]> {
  const rows = await rpc<{
    sale_barn_name: string; state: string; dow: number;
    sale_days: number; typical_head: number; last_sold: string | null;
  }>("rpc_barn_sale_schedule", { p_state: state ?? null });
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((r) => r?.sale_barn_name)
    .map((r) => {
      const name = String(r.sale_barn_name);
      const st = String(r.state ?? "");
      return {
        slug: barnSlug(name, st),
        name,
        state: st,
        dow: Number(r.dow) || 0,
        saleDays: Number(r.sale_days) || 0,
        typicalHead: Number(r.typical_head) || 0,
        lastSold: r.last_sold ? String(r.last_sold) : null,
      };
    });
}

export async function fetchBarnIndex(): Promise<IndexBarn[]> {
  const rows = await rpc<{
    slug: string; name: string; state: string; city: string | null;
    lots: number; head: number; last_sale_date: string | null;
  }>("rpc_ticker_barn_index", {});
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((r) => r?.slug && r?.name)
    .map((r) => {
      const lots = Number(r.lots) || 0;
      const head = Number(r.head) || 0;
      const state = String(r.state ?? "");
      const city = r.city ? String(r.city).trim() : "";
      return {
        slug: String(r.slug),
        name: String(r.name),
        state,
        place: city ? `${city}, ${state}` : state,
        head,
        lots,
        lastSaleDate: r.last_sale_date ?? null,
        lotsLabel: `${num(lots)} ${lots === 1 ? "lot" : "lots"}`,
        headLabel: `${num(head)} hd`,
        lastSaleLabel: shortDay(r.last_sale_date),
        href: `/barn/${r.slug}/ticker`,
      };
    });
}

/** Every reporting state, deepest first. Never throws; returns [] on failure. */
export async function fetchStateIndex(): Promise<IndexState[]> {
  const rows = await rpc<{
    state: string; barns: number; lots: number; head: number; last_sale_date: string | null;
  }>("rpc_ticker_state_index", {});
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((r) => r?.state)
    .map((r) => {
      const alpha = String(r.state).toUpperCase();
      // A state the platform has no name for still gets a row — the alpha is
      // enough to label it, and dropping it would silently hide a whole
      // state's barns. SK (Saskatchewan) is the live case.
      const name = ALPHA_TO_NAME[alpha] ?? alpha;
      const barns = Number(r.barns) || 0;
      const lots = Number(r.lots) || 0;
      const head = Number(r.head) || 0;
      return {
        alpha,
        name,
        slug: slugOfState(name),
        head,
        barns,
        barnsLabel: `${num(barns)} ${barns === 1 ? "barn" : "barns"}`,
        lotsLabel: `${num(lots)} ${lots === 1 ? "lot" : "lots"}`,
        headLabel: `${num(head)} hd`,
        lastSaleLabel: shortDay(r.last_sale_date),
        href: `/ticker/${slugOfState(name)}`,
      };
    })
    // Only states the ticker can actually open. A row whose name we do not
    // know links to /ticker/<alpha>, which resolveStateSlug rejects — better
    // to leave it off the directory than to ship a link to a not-found page.
    .filter((s) => ALPHA_TO_NAME[s.alpha] !== undefined);
}
